/* eslint-disable react-refresh/only-export-components */
import type { ReactNode, SVGProps } from 'react';

/* Inline SVG icon set — stroke-based, 1.6 weight, 16x16 default.
   Ported from the Cafe Receipts design (icons.jsx). */

interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'd'> {
  d: string | ReactNode;
  size?: number;
  sw?: number;
}

const Icon = ({ d, size = 16, fill, stroke = 'currentColor', sw = 1.6, ...rest }: IconProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill={fill || 'none'}
    stroke={stroke}
    strokeWidth={sw}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...rest}
  >
    {typeof d === 'string' ? <path d={d} /> : d}
  </svg>
);

type P = Omit<IconProps, 'd'>;

export const Icons = {
  Dashboard: (p: P) => <Icon {...p} d={<><rect x="3" y="3" width="7" height="9" rx="1.5" /><rect x="14" y="3" width="7" height="5" rx="1.5" /><rect x="14" y="12" width="7" height="9" rx="1.5" /><rect x="3" y="16" width="7" height="5" rx="1.5" /></>} />,
  Receipt: (p: P) => <Icon {...p} d={<><path d="M5 3v18l2-1.5L9 21l2-1.5L13 21l2-1.5L17 21l2-1.5V3l-2 1.5L15 3l-2 1.5L11 3 9 4.5 7 3 5 4.5z" /><path d="M8 8h8M8 12h8M8 16h5" /></>} />,
  Plus: (p: P) => <Icon {...p} d="M12 5v14M5 12h14" />,
  Chart: (p: P) => <Icon {...p} d={<><path d="M3 21h18" /><path d="M6 17V10" /><path d="M11 17V6" /><path d="M16 17v-5" /><path d="M21 17v-9" /></>} />,
  Search: (p: P) => <Icon {...p} d={<><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></>} />,
  Filter: (p: P) => <Icon {...p} d="M3 5h18l-7 9v6l-4-2v-4z" />,
  Calendar: (p: P) => <Icon {...p} d={<><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 9h18M8 3v4M16 3v4" /></>} />,
  Camera: (p: P) => <Icon {...p} d={<><path d="M3 7h3l2-3h8l2 3h3a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1z" /><circle cx="12" cy="13" r="4" /></>} />,
  Upload: (p: P) => <Icon {...p} d={<><path d="M12 16V4" /><path d="m6 10 6-6 6 6" /><path d="M4 20h16" /></>} />,
  X: (p: P) => <Icon {...p} d="M6 6l12 12M18 6 6 18" />,
  ChevronDown: (p: P) => <Icon {...p} d="m6 9 6 6 6-6" />,
  ChevronRight: (p: P) => <Icon {...p} d="m9 6 6 6-6 6" />,
  More: (p: P) => <Icon {...p} d={<><circle cx="5" cy="12" r="1.4" fill="currentColor" /><circle cx="12" cy="12" r="1.4" fill="currentColor" /><circle cx="19" cy="12" r="1.4" fill="currentColor" /></>} sw={0} />,
  LogOut: (p: P) => <Icon {...p} d={<><path d="M9 5H5a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h4" /><path d="m16 17 5-5-5-5" /><path d="M21 12H9" /></>} />,
  Check: (p: P) => <Icon {...p} d="m5 12 4 4 10-10" />,
  Trash: (p: P) => <Icon {...p} d={<><path d="M4 7h16" /><path d="M10 11v6M14 11v6" /><path d="M6 7v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V7" /><path d="M9 7V4h6v3" /></>} />,
  Sparkle: (p: P) => <Icon {...p} d={<><path d="M12 3v4M12 17v4M3 12h4M17 12h4" /><path d="m5.6 5.6 2.8 2.8M15.6 15.6l2.8 2.8M5.6 18.4l2.8-2.8M15.6 8.4l2.8-2.8" /></>} />,
  Mail: (p: P) => <Icon {...p} d={<><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></>} />,
  Lock: (p: P) => <Icon {...p} d={<><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></>} />,
  Download: (p: P) => <Icon {...p} d={<><path d="M12 4v12" /><path d="m6 10 6 6 6-6" /><path d="M4 20h16" /></>} />,
  TrendUp: (p: P) => <Icon {...p} d={<><path d="m3 17 6-6 4 4 8-8" /><path d="M14 7h7v7" /></>} />,
  TrendDown: (p: P) => <Icon {...p} d={<><path d="m3 7 6 6 4-4 8 8" /><path d="M14 17h7v-7" /></>} />,
  Coffee: (p: P) => <Icon {...p} d={<><path d="M4 8h13v6a5 5 0 0 1-5 5H9a5 5 0 0 1-5-5z" /><path d="M17 10h2a2 2 0 0 1 2 2v0a2 2 0 0 1-2 2h-2" /><path d="M8 3c0 1-1 1.5-1 2.5S8 7 8 8M12 3c0 1-1 1.5-1 2.5S12 7 12 8" /></>} />,
  Users: (p: P) => <Icon {...p} d={<><circle cx="9" cy="8" r="3.2" /><path d="M3.5 19a5.5 5.5 0 0 1 11 0" /><path d="M16 5.2a3.2 3.2 0 0 1 0 6" /><path d="M17 14.2A5.5 5.5 0 0 1 20.5 19" /></>} />,
  UserPlus: (p: P) => <Icon {...p} d={<><circle cx="9" cy="8" r="3.2" /><path d="M3.5 19a5.5 5.5 0 0 1 11 0" /><path d="M18 8v6M15 11h6" /></>} />,
  Shield: (p: P) => <Icon {...p} d={<><path d="M12 3 5 6v5c0 4 3 7 7 8 4-1 7-4 7-8V6z" /><path d="m9 12 2 2 4-4" /></>} />,
  Pencil: (p: P) => <Icon {...p} d={<><path d="M4 20h4L19 9l-4-4L4 16z" /><path d="m14 6 4 4" /></>} />,
  Menu: (p: P) => <Icon {...p} d="M4 7h16M4 12h16M4 17h16" />,
};

export type IconName = keyof typeof Icons;
